import Logger from '../../utils/logger';

const DEFAULT_SAR = [1, 1];

function parseSAR(sarStr) {
  if (!sarStr) {
    return DEFAULT_SAR;
  }

  return sarStr.split(':').map(value => parseInt(value, 10));
}

function areEqualSARs(sar1, sar2) {
  return sar1[0] === sar2[0] && sar1[1] === sar2[1];
}

export default class AspectratioService {
  _player = null;
  _isPlayingAds = false;
  _SARs = [[0, DEFAULT_SAR]]; // Array of [offset, SAR] sorted by offset ASC
  _sar = DEFAULT_SAR;
  _scheduledRAF = null;

  _currentRatio = null;
  _currentPadding = null;

  constructor(player) {
    this._player = player;
  }

  start() {
    window.addEventListener('resize', this.updateAspectRatio);
    this._player.addEventListener('fullscreen', this.updateAspectRatio);
    this._player.addEventListener('ontimeupdate', this._onSARUpdate);
    this._player.addEventListener('adsPlaying', this._onAdsPlaying);
    this._player.addEventListener('adsEnded', this._onAdsEnded);
    Logger.debug('Aspectratio service: started');
  }

  async stop() {
    window.removeEventListener('resize', this.updateAspectRatio);
    this._player.removeEventListener('fullscreen', this.updateAspectRatio);
    this._player.removeEventListener('ontimeupdate', this._onSARUpdate);
    this._player.removeEventListener('adsPlaying', this._onAdsPlaying);
    this._player.removeEventListener('adsEnded', this._onAdsEnded);
    Logger.debug('Aspectratio service: stopped');
  }

  // reset back to default aspectratio
  resetSAR() {
    this._SARs = [[0, DEFAULT_SAR]];
    this._onSARUpdate();
  }

  addSAR(sarStr, offset) {
    const newSAR = parseSAR(sarStr);
    const sar = this._pickSAR(offset);

    // drop new SAR if its equal to a last one
    if (areEqualSARs(sar, newSAR)) {
      return;
    }

    Logger.debug(`Aspectratio service: got new SAR ${sarStr} for offset ${offset}`);

    this._SARs.push([offset, newSAR]);

    // sort by offset asc
    this._SARs.sort((a, b) => a[0] - b[0]);

    this._onSARUpdate();
  }

  _onSARUpdate = () => {
    const {
      currentTime,
    } = this._player.model;

    const sar = this._pickSAR(currentTime);
    if (areEqualSARs(sar, this._sar)) {
      return;
    }

    this._sar = sar;
    this.updateAspectRatio();
  }

  _onAdsPlaying = () => {
    this._isPlayingAds = true;
    this.updateAspectRatio();
  };

  _onAdsEnded = () => {
    this._isPlayingAds = false;
    this.updateAspectRatio();
  };

  _pickSAR(time) {
    for (let i = this._SARs.length - 1; i >= 0; i -= 1) {
      const [offset, sar] = this._SARs[i];

      if (time >= offset) {
        return sar;
      }
    }

    throw new Error(`unreachable: can't find SAR for offset ${time}`);
  }

  updateAspectRatio = () => {
    const {
      aspectRatioContainer,
      playerElement,
      adsPlayerElement,
    } = this._player;

    if (this._isPlayingAds) {
      const {
        ratio,
        padding,
      } = this._getAspectRatioAndPadding(adsPlayerElement, aspectRatioContainer, DEFAULT_SAR);

      Logger.debug(`Aspectratio service: new aspect ratio for ads is ${ratio}, new padding is ${padding}`);
      this._scheduleDOMUpdate(adsPlayerElement, aspectRatioContainer, ratio, padding);
      return;
    }

    const {
      ratio,
      padding,
    } = this._getAspectRatioAndPadding(playerElement, aspectRatioContainer, this._sar);

    if (ratio === this._currentRatio && padding === this._currentPadding) {
      return;
    }

    this._currentRatio = ratio;
    this._currentPadding = padding;

    Logger.debug(`Aspectratio service: new aspect ratio is ${ratio}, new padding is ${padding}`);
    this._scheduleDOMUpdate(playerElement, aspectRatioContainer, ratio, padding);
  }

  _getAspectRatioAndPadding = (playerEl, containerEl, sar) => {
    const {
      videoWidth,
      videoHeight,
    } = playerEl;

    if (!videoWidth || !videoHeight) {
      return {
        ratio: 1,
        padding: '',
      };
    }

    const {
      offsetWidth: containerWidth,
      offsetHeight: containerHeight,
    } = containerEl.parentNode;

    const [
      widthSarPoint,
      heightSarPoint,
    ] = sar;

    const heightSar = widthSarPoint / heightSarPoint;

    const targetRatio = (videoWidth * heightSar) / videoHeight;
    const actualRatio = videoWidth / videoHeight;
    const newRatio = Number((targetRatio / actualRatio).toFixed(3));

    let adjustedVideoWidth;
    let adjustedVideoHeight;
    if (targetRatio * containerHeight > containerWidth) {
      adjustedVideoWidth = containerWidth;
      adjustedVideoHeight = containerWidth / targetRatio;
    } else {
      adjustedVideoWidth = targetRatio * containerHeight;
      adjustedVideoHeight = containerHeight;
    }

    const horizontalPadding = Math.ceil((containerWidth - adjustedVideoWidth) / 2);
    const verticalPadding = Math.floor((containerHeight - adjustedVideoHeight) / 2);
    const padding = `${verticalPadding}px ${horizontalPadding}px`;

    return {
      ratio: newRatio,
      padding,
    };
  };

  _scheduleDOMUpdate(playerEl, containerEl, ratio, padding) {
    cancelAnimationFrame(this._scheduledRAF);
    this._scheduledRAF = requestAnimationFrame(() => {
      this._scheduledRAF = null;

      playerEl.style.transform = `scaleX(${ratio})`;
      containerEl.style.padding = padding;
    });
  }
}
